/**
 * AI Completion Plugin
 *
 * Provides AI-powered code completions using the agent service.
 */
import type { editor, languages, IDisposable, Position, CancellationToken } from 'monaco-editor';
import type { EditorPlugin } from './index';
import { api } from '../../services/api';

interface CompletionContext {
  prefix: string;
  suffix: string;
  language: string;
  filePath?: string;
}

// Store disposables for cleanup
let disposables: IDisposable[] = [];
let isEnabled = true;

/**
 * Get completion from AI service.
 */
async function getAICompletion(context: CompletionContext): Promise<string | null> {
  try {
    // E.4 (2026-04-29): post-A.10 Mode 1 OIDC PKCE — backend AuthMiddleware
    // enforces JWT. Route through `api.post()` (services/api.ts) which adds
    // the Authorization header from `localStorage['vibe_designer_token']`.
    const data = await api.post<{ completion?: string }>('/agent/complete', {
      prefix: context.prefix,
      suffix: context.suffix,
      language: context.language,
      file_path: context.filePath,
    });
    return data.completion || null;
  } catch (error) {
    // ApiError on non-200 (auth fail, server error) and network errors both
    // land here; return null so Monaco gracefully falls back to no inline
    // completion.
    console.error('[AI Completion] Error:', error);
    return null;
  }
}

/**
 * Create inline completion provider.
 */
function createInlineCompletionProvider(
  _monaco: typeof import('monaco-editor')
): languages.InlineCompletionsProvider {
  return {
    provideInlineCompletions: async (
      model: editor.ITextModel,
      position: Position,
      _context: languages.InlineCompletionContext,
      _token: CancellationToken
    ): Promise<languages.InlineCompletions> => {
      if (!isEnabled) {
        return { items: [] };
      }

      const lineContent = model.getLineContent(position.lineNumber);
      const prefix = model.getValueInRange({
        startLineNumber: Math.max(1, position.lineNumber - 50),
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });
      const suffix = model.getValueInRange({
        startLineNumber: position.lineNumber,
        startColumn: position.column,
        endLineNumber: Math.min(model.getLineCount(), position.lineNumber + 20),
        endColumn: model.getLineMaxColumn(
          Math.min(model.getLineCount(), position.lineNumber + 20)
        ),
      });

      // Skip if line is empty or just whitespace
      if (!lineContent.trim()) {
        return { items: [] };
      }

      const language = model.getLanguageId();
      const completion = await getAICompletion({
        prefix,
        suffix,
        language,
      });

      if (!completion) {
        return { items: [] };
      }

      return {
        items: [
          {
            insertText: completion,
            range: {
              startLineNumber: position.lineNumber,
              startColumn: position.column,
              endLineNumber: position.lineNumber,
              endColumn: position.column,
            },
          },
        ],
      };
    },
    disposeInlineCompletions: () => {
      // No cleanup needed
    },
  };
}

/**
 * Create completion item provider for traditional completions.
 */
function createCompletionProvider(
  monaco: typeof import('monaco-editor')
): languages.CompletionItemProvider {
  return {
    triggerCharacters: ['.', '(', '<', '"', "'", '/', '@', '#'],
    provideCompletionItems: async (
      model: editor.ITextModel,
      position: Position,
      _context: languages.CompletionContext,
      _token: CancellationToken
    ): Promise<languages.CompletionList> => {
      if (!isEnabled) {
        return { suggestions: [] };
      }

      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      const prefix = model.getValueInRange({
        startLineNumber: Math.max(1, position.lineNumber - 20),
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });

      const language = model.getLanguageId();
      const completion = await getAICompletion({
        prefix,
        suffix: '',
        language,
      });

      if (!completion) {
        return { suggestions: [] };
      }

      // Parse completion into suggestions
      const suggestions: languages.CompletionItem[] = completion
        .split('\n')
        .filter((line) => line.trim())
        .slice(0, 5)
        .map((line, index) => ({
          label: line.trim().substring(0, 50),
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: line,
          range,
          detail: 'AI Suggestion',
          sortText: `0${index}`,
        }));

      return { suggestions };
    },
  };
}

export const aiCompletionPlugin: EditorPlugin = {
  name: 'ai-completion',
  description: 'AI-powered code completions',

  activate(monaco, _editor) {
    // Register inline completion provider for all languages
    const inlineDisposable = monaco.languages.registerInlineCompletionsProvider(
      { pattern: '**/*' },
      createInlineCompletionProvider(monaco)
    );
    disposables.push(inlineDisposable);

    // Register completion provider for common languages
    const languages = [
      'typescript',
      'javascript',
      'typescriptreact',
      'javascriptreact',
      'html',
      'css',
      'scss',
      'json',
      'markdown',
      'python',
    ];

    for (const lang of languages) {
      const completionDisposable = monaco.languages.registerCompletionItemProvider(
        lang,
        createCompletionProvider(monaco)
      );
      disposables.push(completionDisposable);
    }
  },

  deactivate() {
    disposables.forEach((d) => d.dispose());
    disposables = [];
  },

  // Plugin-specific methods
  enable() {
    isEnabled = true;
  },

  disable() {
    isEnabled = false;
  },

  isEnabled() {
    return isEnabled;
  },
} as EditorPlugin & {
  enable: () => void;
  disable: () => void;
  isEnabled: () => boolean;
};
